import { useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from 'components/Kizen/Table';
import { getLink } from 'components/Timeline2/EventBlocks/helpers';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { FIELD_TYPES } from 'utility/constants';
import { useTranslation } from 'react-i18next';
import DateTimeInputInline from 'components/Inputs/inline/DateTimeInput';
import { initiatorTypes } from 'components/Timeline2/constants';
import ActionCell from 'components/Tables/ActionCell';

const ControlsCell = ({ onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const options = useMemo(
    () => [
      {
        label: t('View Details'),
        value: 'view',
      },
    ],
    [t]
  );
  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      width="15px"
      padding="15px"
    />
  );
};

const getRelationshipLabel = ({ type, first_name, last_name, email }, t) => {
  if (type === initiatorTypes.kizen_ai) {
    return t('Kizen AI');
  }

  const label = [first_name, last_name, email && `(${email})`]
    .filter(Boolean)
    .join(' ');
  return label || t('System');
};

const EVENT_DICT = (t) => ({
  role_name_updated: (data) => {
    return t('Name changed from "{{old}}" to "{{new}}"', {
      new: data?.new_name,
      old: data?.old_name,
    });
  },
  role_permission_groups_updated: (data) => {
    const names = data?.new_permission_groups?.map(
      (permission_group) => permission_group.name
    );
    return t('Permission Groups changed to "{{names}}"', {
      names: names?.join(', '),
    });
  },
  permission_group_name_updated: (data) => {
    return t('Name changed from "{{old}}" to "{{new}}"', {
      new: data?.new_name,
      old: data?.old_name,
    });
  },
});

export const useColumns = (handleSelectAction) => {
  const { t } = useTranslation();

  const preReleaseFeatures = usePreReleaseFeatures();
  return useMemo(
    () =>
      [
        {
          id: 'spacer-start',
          headCell: <SizedCell width="15px" />,
          cell: <SizedCell width="15px" />,
          format: () => <span />,
        },
        {
          id: 'team_member',
          headCell: (
            <SortableHeadCell
              hasSorting={false}
              label={t('Team Member')}
              width="200px"
              padding="60px"
            />
          ),
          cell: <SizedCell width="200px" padding="60px" />,
          format: (_, data) => {
            return data.initiator.type === initiatorTypes.employee ? (
              <TextEllipsisWithTooltip
                {...(preReleaseFeatures
                  ? {
                      type: 'anchor',
                      href: getLink(data.initiator, {
                        type: FIELD_TYPES.TeamSelector.type,
                      }),
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                  : {})}
              >
                {getRelationshipLabel(data.initiator, t)}
              </TextEllipsisWithTooltip>
            ) : (
              <TextEllipsisWithTooltip>
                {data.initiator.type === initiatorTypes.kizen_ai
                  ? t('Kizen AI')
                  : t('System')}
              </TextEllipsisWithTooltip>
            );
          },
        },
        {
          id: 'date_modified',
          headCell: (
            <SortableHeadCell
              by="created"
              numeric
              label={t('Modified Date')}
              width="150px"
              padding="60px"
            />
          ),
          cell: <SizedCell width="150px" padding="60px" />,
          format: (_, data) => {
            return (
              <DateTimeInputInline
                readOnly
                value={data.created}
                showDateTooltip
              />
            );
          },
        },
        {
          id: 'changes',
          headCell: (
            <SortableHeadCell
              hasSorting={false}
              label={t('Modification')}
              minWidth="120px"
              padding="60px"
            />
          ),
          cell: <SizedCell minWidth="80px" padding="60px" />,
          format: (_, data) => {
            const matchedItem =
              EVENT_DICT(t)?.[data.type_name] ||
              data.type_display_name ||
              t('Unknown');

            return (
              <TextEllipsisWithTooltip>
                {typeof matchedItem === 'function'
                  ? matchedItem(data?.data)
                  : matchedItem}
              </TextEllipsisWithTooltip>
            );
          },
        },
        handleSelectAction && {
          id: 'actions',
          headCell: (
            <SortableHeadCell
              hasSorting={false}
              label={' '}
              width="15px"
              padding="15px"
            />
          ),
          cell: <ControlsCell onSelectAction={handleSelectAction} />,
        },
      ].filter(Boolean),
    [preReleaseFeatures, t, handleSelectAction]
  );
};
